/*
 * Keeps the Klarna on-site message in sync with the selected variant.
 * Spec: docs/pdp-spec.md.
 *
 * Why this is needed: carat runs 1.0ct to 5.0ct on these rings, so switching variant moves the
 * price a long way. Klarna renders the monthly figure from `data-purchase-amount`, which it
 * reads once — without refreshing it, a shopper on 5.0ct sees the instalment for 1.0ct.
 *
 * Hooks Dawn's own `variant-change` event via the global `subscribe()`. The event name is the
 * string literal because PUB_SUB_EVENTS is module-scoped in constants.js and not on `window`.
 * If that ever changes, the DOM fallback below still catches it.
 */
(function () {
  const VARIANT_CHANGE = 'variant-change';

  function init() {
    const wrapper = document.querySelector('[data-kardima-klarna]');
    if (!wrapper) return;

    const placement = wrapper.querySelector('klarna-placement');
    if (!placement) return;

    /**
     * @param {number|null} amount price in MINOR units (øre), or null when no variant is
     *   selectable — in which case the message is hidden rather than left showing a stale price.
     */
    function update(amount) {
      if (amount == null) {
        wrapper.hidden = true;
        return;
      }

      wrapper.hidden = false;

      // Klarna re-renders every placement on the page, so skip no-op refreshes.
      if (placement.getAttribute('data-purchase-amount') === String(amount)) return;
      placement.setAttribute('data-purchase-amount', String(amount));

      // Optional chaining throughout: the SDK is injected async by the Klarna app, so it may
      // not have arrived yet on a fast variant switch. It reads the attribute on first render,
      // so a missed refresh is self-correcting rather than a wrong number.
      window.Klarna?.OnsiteMessaging?.refresh?.();
    }

    if (typeof window.subscribe === 'function') {
      window.subscribe(VARIANT_CHANGE, (event) => {
        const variant = event?.data?.variant;
        update(variant && typeof variant.price === 'number' ? variant.price : null);
      });
      return;
    }

    /*
     * Fallback: Dawn sets the hidden id input and dispatches a bubbling `change` on it.
     * That gives us the variant id but not the price, so it is resolved from the product JSON
     * the section already ships — no extra request.
     */
    const productInfo = document.querySelector('product-info');
    if (!productInfo) return;

    productInfo.addEventListener('change', (event) => {
      const input = event.target;
      if (!input || input.name !== 'id') return;

      const json = document.querySelector('[id^="ProductJSON-"], [data-product-json]');
      if (!json) return;

      try {
        const variants = JSON.parse(json.textContent).variants || [];
        const match = variants.find((v) => String(v.id) === String(input.value));
        update(match ? match.price : null);
      } catch (error) {
        /* Leave the last known good amount rather than showing a wrong one. */
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
